import Cookies from 'js-cookie';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import { Hub, HubCapsule } from '@aws-amplify/core';
import { Auth, CognitoUser } from '@aws-amplify/auth';

import {
	COGNITO_CHALLENGE_FLOW,
	getCognitoChallengeFlow,
	getEncryptedTokens,
	handleDangerousHtmlClick,
	trackAction,
	toErrorFields,
} from '@/lib/utils';
import config, { isHC } from '@/lib/config';
import { ACTION_STATUS, ACTION_TYPES, COOKIES } from '@/lib/models';
import { accountService, publicService } from '@/lib/services';
import {
	createUseThemedStyles,
	useAutoSignInContext,
	useHandleError,
	usePlatformsContext,
	useRedirectUser,
} from '@/hooks';
import {
	LoadingButton,
	MfaCodeEntryModal,
	NewPasswordRequiredModal,
	MfaFlowModals,
	ExpandingCarousel,
	PlatformSelectionModal,
	HTML_CONTENT_ANCHOR,
	CONTROL_TINT,
	Banner,
} from '@/components';
import { mediaQueries } from '@/jss';

import { ReactComponent as HackettLogo } from '@/assets/logos/logo-hackett-w-tag.svg';

import genAiExecutiveAdvisory from '@/assets/images/gen_ai_executive_advisory.jpg';
import aiBg from '@/assets/images/ai-bg.png';
import aiXplrBg from '@/assets/images/hackett-ai-xplr-bg.png';
import aiXplrSlide from '@/assets/images/hackett-ai-xplr-process.png';
import ad2Bg from '@/assets/images/ad2_bg.png';
import ad3Bg from '@/assets/images/ad3_bg.jpg';
import searchBox from '@/assets/images/search_box.png';

import { ReactComponent as BlogIcon } from '@/assets/icons/icon-blog.svg';
import { ReactComponent as ChevronRightIcon } from '@/assets/icons/icon-chevron-right.svg';
import { getRememberThisDevice, setRememberThisDevice } from '@/components/remember-device-field';

const useStyles = createUseThemedStyles((theme) => ({
	hackettConnectLogo: {
		width: '75%',
		height: 'auto',
	},
	logInFormOuter: {
		width: '90%',
		margin: '0 auto',
		[mediaQueries.md]: {
			width: '60%',
			maxWidth: 540,
		},
		[mediaQueries.xl]: {
			width: '75%',
		},
	},
	carouselOuter: {
		display: 'none',
		[mediaQueries.xl]: {
			width: '100%',
			height: '100%',
			minHeight: 750,
			display: 'block',
		},
	},
	logInForm: {
		padding: 25,
		marginBottom: 40,
		backgroundColor: theme.colors.gray100,
		border: `1px solid ${theme.colors.gray300}`,
	},
	hackettGroupLogo: {
		width: '75%',
	},
	hackettConnectSup: {
		fontSize: '50%',
		top: '-.75em',
	},
	'@global': {
		body: {
			backgroundColor: theme.colors.white,
		},
	},
}));

export const SignIn = () => {
	const redirectUser = useRedirectUser();
	const classes = useStyles();
	const handleError = useHandleError();
	const { autoSignInUser, setAutoSignInUser, setEmailAddress, setPassword } = useAutoSignInContext();

	const [isLoading, setIsLoading] = useState(false);
	const [emailInputValue, setEmailInputValue] = useState<string>('');
	const [passwordInputValue, setPasswordInputValue] = useState<string>('');
	const [user, setUser] = useState<CognitoUser>();

	const [isFirstTimeSetup, setIsFirstTimeSetup] = useState(false);
	const [showMfaFlow, setShowMfaFlow] = useState(false);
	const [showNewPasswordRequiredModal, setShowNewPasswordRequiredModal] = useState(false);
	const [showMfaCodeEntryModal, setShowMfaCodeEntryModal] = useState(false);

	const [platformSelectionUser, setPlatformSelectionUser] = useState<CognitoUser>();
	const [showPlatformSelectionModal, setShowPlatformSelectionModal] = useState(false);
	const { platforms } = usePlatformsContext();
	const platform = useMemo(() => platforms[0], [platforms]);

	useEffect(() => {
		if (Cookies.get(COOKIES.UPDATE_CREDENTIALS_LINK_CLICKED)) {
			return;
		}
	}, []);

	async function handleSignInFormSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();

		try {
			setIsLoading(true);

			if (config.HACKETT_USER_AUTH_USE_REAL_AUTH === 'true') {
				const cognitoUser: CognitoUser = await Auth.signIn(
					emailInputValue.replace(' ', '').trim(),
					passwordInputValue
				);
				const challengesCleared = cognitoUserHasClearedChallenges(cognitoUser);

				// If challengesCleared is true, the Hub will handle the signIn redirect.
				// If not, we still need to set the user state to pass to the modals as a prop.
				if (!challengesCleared) {
					setUser(cognitoUser);
				}
			} else {
				const { token } = await publicService.authenticate({ emailAddress: emailInputValue }).fetch();
				redirectUser({ accessToken: token });
			}
		} catch (error) {
			trackAction({
				actionType: ACTION_TYPES.ACCOUNT_SIGNED_IN,
				status: ACTION_STATUS.FAILURE,
				emailAddress: emailInputValue,
				...toErrorFields(error),
			});
			handleError(error);
		} finally {
			setIsLoading(false);
		}
	}

	// Check user challenges and open appropriate modals.
	const cognitoUserHasClearedChallenges = useCallback((cognitoUser: CognitoUser) => {
		const challengeFlow = getCognitoChallengeFlow(cognitoUser.challengeName);
		setIsFirstTimeSetup(challengeFlow === COGNITO_CHALLENGE_FLOW.MFA_SETUP);

		switch (challengeFlow) {
			case COGNITO_CHALLENGE_FLOW.MFA_SETUP:
				setShowMfaFlow(true);
				return false;
			case COGNITO_CHALLENGE_FLOW.MFA_CODE:
				setShowMfaCodeEntryModal(true);
				return false;
			case COGNITO_CHALLENGE_FLOW.NEW_PASSWORD:
				setShowNewPasswordRequiredModal(true);
				return false;
			case COGNITO_CHALLENGE_FLOW.COMPLETE:
			default:
				return true;
		}
	}, []);

	// If we have an automatically signed in user from the accept-invite.tsx page,
	// check if their challenges have been cleared, and assign data accordingly.
	useEffect(() => {
		if (!autoSignInUser) {
			return;
		}

		const challengesCleared = cognitoUserHasClearedChallenges(autoSignInUser);

		// If challengesCleared is true, the Hub will handle the signIn redirect.
		// If not, we still need to set the user state to pass to the modals as a prop.
		if (!challengesCleared) {
			setUser(autoSignInUser);
			setAutoSignInUser(undefined);
		}
	}, [autoSignInUser, cognitoUserHasClearedChallenges, setAutoSignInUser]);

	const handleRememberDevice = useCallback(async (cognitoUser: CognitoUser) => {
		const shouldRemember = getRememberThisDevice();
		if (!shouldRemember) return;

		await Auth.rememberDevice();
		const token = cognitoUser
			.getSignInUserSession()
			?.getIdToken()
			.getJwtToken();

		// this key exists but it's not typed on CognitoUser type
		const deviceKey: string = (cognitoUser as any)?.deviceKey;
		if (!token || !deviceKey) return;

		await accountService.rememberThisDevice(token, deviceKey);

		const idTokenPayload = cognitoUser?.getSignInUserSession?.()?.getIdToken?.()?.payload;
		trackAction({
			actionType: ACTION_TYPES.DEVICE_REMEMBERED,
			status: ACTION_STATUS.SUCCESS,
			emailAddress: idTokenPayload?.email,
			cognitoUserId: idTokenPayload?.sub,
		});

		return deviceKey;
	}, []);

	// AWS Amplify Hub handles listening to 'auth' events.
	// https://docs.amplify.aws/lib/auth/auth-events/q/platform/js/
	useEffect(() => {
		const handleHubAuthEvent = async ({ payload }: HubCapsule) => {
			const { event, data } = payload;

			// If a 'signIn' event is detected, either from sign in, code entry, or MFA clearance,
			// extract the token from the payload and redirect the user to their destination.
			// Also clear out all context data just in case.
			if (event === 'signIn') {
				const idTokenPayload = data?.getSignInUserSession?.()?.getIdToken?.()?.payload;
				trackAction({
					actionType: ACTION_TYPES.ACCOUNT_SIGNED_IN,
					status: ACTION_STATUS.SUCCESS,
					emailAddress: idTokenPayload?.email,
					cognitoUserId: idTokenPayload?.sub,
				});

				try {
					const encryptedTokens = await getEncryptedTokens(data);

					let deviceKey;
					if (config.HACKETT_USER_AUTH_REMEMBER_DEVICE_ENABLED) {
						deviceKey = await handleRememberDevice(data);
					}

					if (!deviceKey) {
						data.getCachedDeviceKeyAndPassword();
						deviceKey = data.deviceKey;
					}

					redirectUser(encryptedTokens, deviceKey);
				} catch (_error) {
					setPlatformSelectionUser(data);
					setShowPlatformSelectionModal(true);
				} finally {
					setAutoSignInUser(undefined);
					setEmailAddress('');
					setPassword('');
					setEmailInputValue('');
					setPasswordInputValue('');
					setRememberThisDevice(false);
				}
			}
		};

		Hub.listen('auth', handleHubAuthEvent);

		return () => Hub.remove('auth', handleHubAuthEvent);
	}, [platform, redirectUser, setAutoSignInUser, setEmailAddress, setPassword, handleRememberDevice]);

	const handleResendCode = async () => {
		if (config.HACKETT_USER_AUTH_USE_REAL_AUTH !== 'true') {
			return;
		}
		const refreshedUser: CognitoUser = await Auth.signIn(
			emailInputValue.replace(' ', '').trim(),
			passwordInputValue
		);
		const challengesCleared = cognitoUserHasClearedChallenges(refreshedUser);

		if (!challengesCleared) {
			setUser(refreshedUser);
		}
	};

	return (
		<>
			{user && (
				<>
					<MfaFlowModals
						user={user}
						show={showMfaFlow}
						firstTimeSetup={isFirstTimeSetup}
						onHide={() => {
							setShowMfaFlow(false);
						}}
						onComplete={() => {
							setShowMfaFlow(false);
							// Auth Hub will handle successful sign in
						}}
					/>

					<NewPasswordRequiredModal
						user={user}
						show={showNewPasswordRequiredModal}
						onHide={() => {
							setShowNewPasswordRequiredModal(false);
						}}
						onSuccess={(loggedUser) => {
							setShowNewPasswordRequiredModal(false);
							setPassword('');
							setPasswordInputValue('');
							// Auth Hub will handle successful sign in
						}}
					/>

					<MfaCodeEntryModal
						user={user}
						show={showMfaCodeEntryModal}
						onHide={() => {
							setShowMfaCodeEntryModal(false);
						}}
						onSuccess={() => {
							setShowMfaCodeEntryModal(false);
							// Auth Hub will handle successful sign in
						}}
						onResendCode={handleResendCode}
					/>
				</>
			)}

			{platformSelectionUser && (
				<PlatformSelectionModal
					user={platformSelectionUser}
					show={showPlatformSelectionModal}
					onHide={() => {
						setShowPlatformSelectionModal(false);
					}}
				/>
			)}

			{isHC && <Banner />}

			<Container>
				<Row>
					<Col xl={6} className="py-11">
						<div className={classes.logInFormOuter}>
							<h1 className="mb-6 text-primary text-center">
								{platform?.headerIconUrl && (
									<img
										src={platform.headerIconUrl}
										alt={platform.platformName}
										style={{ margin: '0 auto', maxWidth: '75%' }}
									/>
								)}
							</h1>
							<Form className={classes.logInForm} onSubmit={handleSignInFormSubmit}>
								<h2 className="mb-4 text-secondary">Log in</h2>
								<div dangerouslySetInnerHTML={{ __html: platform?.ctaHtmlTop ?? '' }} />
								<div
									dangerouslySetInnerHTML={{ __html: platform?.ctaHtmlBottom ?? '' }}
									onClick={handleDangerousHtmlClick}
								/>

								<Form.Group className="mb-4" controlId="sign-in-email">
									<Form.Label>Email</Form.Label>
									<Form.Control
										type="email"
										placeholder="Enter your email"
										value={emailInputValue}
										onChange={({ currentTarget }) => {
											setEmailAddress(currentTarget.value);
											setEmailInputValue(currentTarget.value);
										}}
										required
									/>
								</Form.Group>
								<Form.Group className="mb-2" controlId="sign-in-password">
									<Form.Label>Password</Form.Label>
									<Form.Control
										type="password"
										placeholder="Password"
										value={passwordInputValue}
										onChange={({ currentTarget }) => {
											setPassword(currentTarget.value);
											setPasswordInputValue(currentTarget.value);
										}}
										required={config.HACKETT_USER_AUTH_USE_REAL_AUTH === 'true'}
									/>
								</Form.Group>
								<div className="mb-6 text-right">
									<Link to="/forgot-password">Forgot password?</Link>
								</div>
								<div>
									<LoadingButton
										isLoading={isLoading}
										variant="success"
										type="submit"
										className="w-100 d-block"
									>
										Log In
									</LoadingButton>
								</div>
							</Form>
							{isHC && config.HACKETT_USER_AUTH_BLOG_URL && (
								<>
									<Button
										className="p-4 mb-6 d-flex align-items-center justify-content-center w-100"
										onClick={() => {
											window.open(config.HACKETT_USER_AUTH_BLOG_URL, '_blank', 'noopener');
										}}
									>
										<BlogIcon className="mr-4" width={30} height={30} />
										Check out our new blog posts!
										<ChevronRightIcon className="ml-4" width={12} height={12} />
									</Button>
									<div className="mb-4 text-center">
										<a
											href="https://www.thehackettgroup.com/hackett-connect/"
											target="_blank"
											rel="noreferrer"
										>
											Learn more about Hackett Connect
											<sup className={classes.hackettConnectSup}>&reg;</sup>
										</a>
									</div>
								</>
							)}
							<h1 className="text-center">
								<HackettLogo className={classes.hackettGroupLogo} />
							</h1>
						</div>
					</Col>
					<Col xl={6}>
						<div className={classes.carouselOuter}>
							<ExpandingCarousel
								slides={[
									...(isHC
										? [
												{
													imageUrl: '',
													htmlContent: `
													<h1 class="mb-4 text-white font-weight-regular"><strong>Answers,</strong><br/>When You Need Them</h1>
													<p class="mb-6 text-white"><strong>AskHackett&trade;</strong> provides instant, fact-based guidance based on Hackett Intelligence to help you validate decisions, explore options and keep priorities moving.</p>
													<img class="mb-8" src="${searchBox}" alt="Search Box"/>
												`,
													action: {
														variant: 'info',
														title: 'Ask Now',
														onClick: () => {
															window.open(
																'https://advisory.poweredbyhackett.com/ask-hackett',
																'_blank',
																'noopener'
															);
														},
													},
													htmlContentAnchor: HTML_CONTENT_ANCHOR.CENTER,
												},
												{
													imageUrl: ad2Bg,
													htmlContent: `
													<h1 class="mb-4 text-white font-weight-regular">Turn AI Ambition<br/>Into <strong>Advantage</strong></h1>
													<p class="mb-8 text-white">Applied Intelligence helps you move from ideas to impact - with continuous, export-led guidance embedded in your decision flow. Accelerate execution, improve outcomes and scale what works.</p>
												`,
													action: {
														variant: 'secondary',
														title: 'Learn How',
														onClick: () => {
															window.open(
																'https://www.thehackettgroup.com/applied-intelligence-programs/',
																'_blank',
																'noopener'
															);
														},
													},
													htmlContentAnchor: HTML_CONTENT_ANCHOR.CENTER,
												},
												{
													imageUrl: ad3Bg,
													htmlContent: `
													<h1 class="mb-4 text-white text-center font-weight-bold"><span class="text-info">New Blog Posts.</span><br/><span class="text-primary">Fresh Insight.</span></h1>
													<p class="mb-8 text-dark text-center">Discover timely perspectives to help you<br/>make smarter decisions, faster.</p>
												`,
													action: {
														variant: 'secondary',
														title: 'Explore Posts',
														onClick: () => {
															window.open(
																config.HACKETT_USER_AUTH_BLOG_URL,
																'_blank',
																'noopener'
															);
														},
													},
													htmlContentAnchor: HTML_CONTENT_ANCHOR.CENTER,
													controlTint: CONTROL_TINT.DARK,
												},
										  ]
										: []),
									{
										imageUrl: genAiExecutiveAdvisory,
										htmlContent: `
											<h1 class="mb-0 text-white font-weight-regular">Just Launched:</h1>
											<h1 class="mb-4 text-white">Gen AI Executive Applied Intelligence</h1>
											<p class="mb-8 text-white"><span class="font-weight-semi-bold">Our newest program is here</span> - built for leaders ready to scale Gen AI, with purpose, speed, and precision.</p>
										`,
										action: {
											variant: 'info',
											title: 'Learn More',
											onClick: () => {
												window.open(
													'https://www.thehackettgroup.com/the-hackett-group-launches-gen-ai-focused-executive-advisory-program-for-enterprises/',
													'_blank',
													'noopener'
												);
											},
										},
										htmlContentAnchor: HTML_CONTENT_ANCHOR.BOTTOM,
									},
									{
										imageUrl: aiXplrBg,
										htmlContent: `
											<h1 class="mb-8 text-white text-center">AI Designed for Real Impact</h1>
											<img class="mb-8" src="${aiXplrSlide}" style="max-width: 100%; display: block; margin: 0 auto;" alt="" />
										`,
										action: {
											variant: 'dark',
											title: 'Schedule Demo!',
											onClick: () => {
												window.open(
													'https://go.poweredbyhackett.com/conusaixplr2402hc',
													'_blank',
													'noopener'
												);
											},
										},
									},
									{
										imageUrl: aiBg,
										htmlContent: `
											<h1 class="mb-40 text-white text-center font-weight-bold">ZBrain<sup>&#8482;</sup> Integration Powers Complete AI Solutions</h1>
											<h3 class="text-white text-center font-weight-normal">
												The acquisition of LeewayHertz and its ZBrain<sup>&#8482;</sup> platform integration delivers end-to-end AI solutions, from strategic ideation to seamless implementation.
											</h3>
										`,
										action: {
											variant: 'info',
											title: 'Learn More',
											onClick: () => {
												window.open(
													'https://www.thehackettgroup.com/the-hackett-group-announces-strategic-acquisition-of-leading-gen-ai-development-firm-leewayhertz/',
													'_blank',
													'noopener'
												);
											},
										},
										slideContentStyle: { transform: 'translateY(20px)' },
									},
								]}
							/>
						</div>
					</Col>
				</Row>
			</Container>
		</>
	);
};
